import { supabase } from '../lib/supabase';
import { orgStore } from '../lib/orgStore';
import { requireOrgId, stripImmutable } from '../lib/orgScope';

export type ThemeSource = 'manual' | 'template';

export interface Theme {
  id: string;
  org_id: string;
  project_id: string | null;
  name: string;
  description: string | null;
  parent_id: string | null;
  source: ThemeSource;
  template_id: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  parent?: { id: string; name: string } | null;
}

// ── DB row shapes (snake_case as Postgres returns them) ─────────
export type TemplateIcon = 'box' | 'heart' | 'whiteboard';
export type CategoryIcon  = 'all' | 'box' | 'whiteboard' | 'heart';

interface ThemeTemplateRow {
  id: string;
  category_id: string;
  title: string;
  description: string;
  icon_type: TemplateIcon;
  keywords: string[];
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface ThemeCategoryRow {
  id: string;
  label: string;
  icon: CategoryIcon;
  sort_order: number;
  created_at: string;
}

// ── UI-facing types (camelCase, denormalized) ───────────────────
export interface ThemeTemplate {
  id: string;
  title: string;
  description: string;
  keywords: string[];
  category: string;
  categoryLabel: string;
  icon: TemplateIcon;
  sortOrder: number;
}

export interface ThemeCategory {
  id: string;
  label: string;
  icon: CategoryIcon;
  sortOrder: number;
}

export interface TemplateUpsertInput {
  title: string;
  description: string;
  keywords: string[];
  category_id: string;
  icon_type: TemplateIcon;
  sort_order: number;
}

// ── Theme CRUD (direct Supabase + RLS) ───────────────────────────
const THEME_SELECT = '*, parent:parent_id (id, name)';

let _themesCache: { key: string; data: Theme[]; expiry: number } | null = null;
const CACHE_TTL = 60_000;
function invalidateThemesCache() { _themesCache = null; }

type ThemeMutableFields = Pick<Theme, 'name' | 'description' | 'parent_id' | 'source' | 'template_id'>;

export const themeService = {
  async getThemes(projectId?: string | null): Promise<Theme[]> {
    const key = projectId ?? '__all__';
    const now = Date.now();
    if (_themesCache && _themesCache.key === key && now < _themesCache.expiry) {
      return _themesCache.data;
    }
    let query = supabase.from('themes').select(THEME_SELECT).order('created_at', { ascending: false });
    if (projectId) query = query.eq('project_id', projectId);
    const { data, error } = await query;
    if (error) throw error;
    const rows = (data ?? []) as unknown as Theme[];
    _themesCache = { key, data: rows, expiry: now + CACHE_TTL };
    return rows;
  },

  async getTheme(id: string): Promise<Theme> {
    const { data, error } = await supabase.from('themes').select(THEME_SELECT).eq('id', id).single();
    if (error) throw error;
    return data as unknown as Theme;
  },

  async createTheme(input: {
    name: string;
    description?: string | null;
    project_id?: string | null;
    parent_id?: string | null;
    source?: ThemeSource;
    template_id?: string | null;
  }): Promise<Theme> {
    const orgId = orgStore.getOrgId();
    if (!orgId) throw new Error('No organization found.');
    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.id) throw new Error('You must be signed in to perform this action.');
    const { data, error } = await supabase
      .from('themes')
      .insert({
        org_id: orgId,
        created_by: user.id,
        name: input.name,
        description: input.description ?? null,
        project_id: input.project_id ?? null,
        parent_id: input.parent_id ?? null,
        source: input.source ?? 'manual',
        template_id: input.template_id ?? null,
      })
      .select(THEME_SELECT)
      .single();
    if (error) throw error;
    invalidateThemesCache();
    return data as unknown as Theme;
  },

  async updateTheme(id: string, updates: Partial<ThemeMutableFields>): Promise<Theme> {
    const { data, error } = await supabase
      .from('themes')
      .update({ ...stripImmutable(updates), updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('org_id', requireOrgId())
      .select(THEME_SELECT)
      .single();
    if (error) throw error;
    invalidateThemesCache();
    return data as unknown as Theme;
  },

  async deleteTheme(id: string): Promise<void> {
    const { error } = await supabase
      .from('themes')
      .delete()
      .eq('id', id)
      .eq('org_id', requireOrgId());
    if (error) throw error;
    invalidateThemesCache();
  },
};

// ── Template CRUD (reads/writes Supabase directly) ───────────────
//
// Templates are global/curated content, not per-org. The `theme_templates`
// table is publicly readable (RLS: true) and only writable by org
// owners/admins (see sql/theme_templates.sql). Mutations invalidate the
// template cache so the next read reflects the change.

let _templatesCache: { data: ThemeTemplate[]; expiry: number } | null = null;
let _categoriesCache: { data: ThemeCategory[]; expiry: number } | null = null;
const TEMPLATE_TTL = 60_000;

function invalidateTemplateCaches() {
  _templatesCache = null;
  _categoriesCache = null;
}

function rowToTemplate(row: ThemeTemplateRow, categories: ThemeCategory[]): ThemeTemplate {
  const cat = categories.find((c) => c.id === row.category_id);
  return {
    id: row.id,
    title: row.title,
    description: row.description,
    keywords: row.keywords ?? [],
    category: row.category_id,
    categoryLabel: cat?.label ?? row.category_id,
    icon: row.icon_type,
    sortOrder: row.sort_order,
  };
}

async function fetchCategoriesFromDb(): Promise<ThemeCategory[]> {
  if (_categoriesCache && Date.now() < _categoriesCache.expiry) return _categoriesCache.data;
  const { data, error } = await supabase
    .from('theme_categories')
    .select('id, label, icon, sort_order, created_at')
    .order('sort_order', { ascending: true });
  if (error) throw error;
  const categories: ThemeCategory[] = (data ?? []).map((r: ThemeCategoryRow) => ({
    id: r.id, label: r.label, icon: r.icon, sortOrder: r.sort_order,
  }));
  _categoriesCache = { data: categories, expiry: Date.now() + TEMPLATE_TTL };
  return categories;
}

export const templateService = {
  /** All categories (for filter chips and the picker in the editor). */
  async getCategories(): Promise<ThemeCategory[]> {
    return fetchCategoriesFromDb();
  },

  /** All templates, joined with their category label for display. */
  async getTemplates(): Promise<ThemeTemplate[]> {
    if (_templatesCache && Date.now() < _templatesCache.expiry) return _templatesCache.data;

    const [categories, { data, error }] = await Promise.all([
      fetchCategoriesFromDb(),
      supabase
        .from('theme_templates')
        .select('id, category_id, title, description, icon_type, keywords, sort_order, created_at, updated_at')
        .order('sort_order', { ascending: true }),
    ]);
    if (error) throw error;
    const templates = (data ?? []).map((r: ThemeTemplateRow) => rowToTemplate(r, categories));
    _templatesCache = { data: templates, expiry: Date.now() + TEMPLATE_TTL };
    return templates;
  },

  /** Single template by id, or null if not found. */
  async getTemplate(id: string): Promise<ThemeTemplate | null> {
    const all = await this.getTemplates();
    return all.find((t) => t.id === id) ?? null;
  },

  /** Create a new template. RLS requires the caller to be an org owner/admin. */
  async createTemplate(input: TemplateUpsertInput): Promise<ThemeTemplate> {
    const { data, error } = await supabase
      .from('theme_templates')
      .insert({
        title: input.title,
        description: input.description,
        keywords: input.keywords,
        category_id: input.category_id,
        icon_type: input.icon_type,
        sort_order: input.sort_order,
      })
      .select('id, category_id, title, description, icon_type, keywords, sort_order, created_at, updated_at')
      .single();
    if (error) throw error;
    invalidateTemplateCaches();
    const categories = await fetchCategoriesFromDb();
    return rowToTemplate(data as ThemeTemplateRow, categories);
  },

  /** Patch an existing template. Partial — any subset of editable fields. */
  async updateTemplate(id: string, updates: Partial<TemplateUpsertInput>): Promise<ThemeTemplate> {
    const { data, error } = await supabase
      .from('theme_templates')
      .update(updates)
      .eq('id', id)
      .select('id, category_id, title, description, icon_type, keywords, sort_order, created_at, updated_at')
      .single();
    if (error) throw error;
    invalidateTemplateCaches();
    const categories = await fetchCategoriesFromDb();
    return rowToTemplate(data as ThemeTemplateRow, categories);
  },

  /** Permanently delete a template. Cascades are off — the row just disappears. */
  async deleteTemplate(id: string): Promise<void> {
    const { error } = await supabase.from('theme_templates').delete().eq('id', id);
    if (error) throw error;
    invalidateTemplateCaches();
  },

  /** Drop the in-memory caches (e.g. after a cross-tab mutation). */
  invalidateCaches(): void {
    invalidateTemplateCaches();
  },
};
